import { DropdownSelect } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import type {
  PropsWithChildren,
  PropsWithClassName,
} from 'lib-shared-types-utils';
import { useCallback, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';

import type {
  FlowCategoriesT,
  FlowCategoryT,
} from 'components/powerFlowShared/helpers/flowVariableHelpers';

import { VariableSelectorItem } from './components/item/item';
import type { VariableDropdownItem } from './types';

type Props = PropsWithChildren &
  PropsWithClassName & {
    withSearch?: boolean;
    variables: FlowCategoriesT;
    onSelect: (name: string, label: string) => void;
  };

export const VariableSelector = ({
  className,
  variables,
  children,
  withSearch,
  onSelect,
  ...otherProps
}: Props): JSX.Element => {
  const handleSelect = useCallback(
    (variable: VariableDropdownItem) => {
      if ('content' in variable) {
        onSelect(variable.name, variable.content);
      }
    },
    [onSelect],
  );

  const [search, setSearch] = useState<string | undefined>('');

  const filteredItems = useMemo(() => {
    const items: VariableDropdownItem[] = [];

    variables.forEach((variable: FlowCategoryT) => {
      const filteredItems = variable.items
        .filter((variable) => {
          if (!!search && typeof variable.name === 'string') {
            return (
              variable.name.toLowerCase().indexOf(search.toLowerCase()) !== -1
            );
          }
          return true;
        })
        .map((variable) => ({
          ...variable,
          id: typeof variable.name === 'string' ? variable.name : uuid(),
          matched: search,
        }));

      if (items.length > 0 && filteredItems.length) {
        items.push({
          id: variable.label,
          name: variable.label,
          divider: true,
        });
      }

      items.push(...filteredItems);
    });

    return items;
  }, [variables, search]);

  const rootClass = classnames(className);

  return (
    <div {...otherProps} className={rootClass}>
      <DropdownSelect<VariableDropdownItem>
        itemComponent={VariableSelectorItem}
        items={filteredItems}
        noStyle
        hasSearch={withSearch}
        onSearch={setSearch}
        onSelect={handleSelect}
        fixed
        containerProps={{ style: { width: '300px' } }}
      >
        {children}
      </DropdownSelect>
    </div>
  );
};
